import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  schemas,
  type CatalogHardwareDetails,
  type CreateCatalogHardwareRequest,
} from '@kreart/shared';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Form, TextField, SelectField, CheckboxField } from '@/components/form';
import { nonNegativeIntegerFieldProps } from '@/components/form/numberInputProps';
import { Button } from '@/components/ui/button';
import { integerStringField } from './integerStringField';

interface HardwareFormValues {
  name: string;
  offerName: string;
  sku: string;
  internalCode: string;
  manufacturer: string;
  pricePerPiece: string;
  currency: 'RON' | 'EUR' | 'USD';
  coefficient: string;
  timeCircularSec: string;
  timeCncSec: string;
  timeCantSec: string;
  timeAssemblySec: string;
  active: boolean;
}

const EMPTY: HardwareFormValues = {
  name: '',
  offerName: '',
  sku: '',
  internalCode: '',
  manufacturer: '',
  pricePerPiece: '',
  currency: 'RON',
  coefficient: '2.50',
  timeCircularSec: '0',
  timeCncSec: '0',
  timeCantSec: '0',
  timeAssemblySec: '0',
  active: true,
};

/** Mirrors the `nonNegativeInt` cap in the shared hardware schema. */
const MAX_SECONDS = 86400;

/** Typed as numbers on the wire, held as strings here — see the helper. */
const secondsField = integerStringField(MAX_SECONDS, 'seconds');

const hardwareFormSchema = schemas.createCatalogHardwareRequest.extend({
  timeCircularSec: secondsField,
  timeCncSec: secondsField,
  timeCantSec: secondsField,
  timeAssemblySec: secondsField,
});

const CURRENCY_OPTIONS = [
  { value: 'RON', label: 'RON' },
  { value: 'EUR', label: 'EUR' },
  { value: 'USD', label: 'USD' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  initial: CatalogHardwareDetails | null;
  onSubmit: (body: CreateCatalogHardwareRequest) => Promise<void>;
}

export function HardwareEditDrawer({ open, onClose, initial, onSubmit }: Props) {
  const { t } = useTranslation();
  const form = useForm<HardwareFormValues>({
    resolver: zodResolver(hardwareFormSchema),
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!open) return;
    if (!initial) {
      form.reset(EMPTY);
      return;
    }
    form.reset({
      name: initial.name,
      offerName: initial.offerName ?? '',
      sku: initial.sku ?? '',
      internalCode: initial.internalCode ?? '',
      manufacturer: initial.manufacturer ?? '',
      pricePerPiece: initial.pricePerPiece ?? '',
      currency: initial.currency,
      coefficient: initial.coefficient,
      timeCircularSec: String(initial.timeCircularSec),
      timeCncSec: String(initial.timeCncSec),
      timeCantSec: String(initial.timeCantSec),
      timeAssemblySec: String(initial.timeAssemblySec),
      active: initial.active,
    });
  }, [open, initial, form]);

  const onValid = async (values: HardwareFormValues) => {
    await onSubmit({
      name: values.name,
      offerName: values.offerName || undefined,
      sku: values.sku || undefined,
      internalCode: values.internalCode || undefined,
      manufacturer: values.manufacturer || undefined,
      pricePerPiece: values.pricePerPiece,
      currency: values.currency,
      coefficient: values.coefficient,
      timeCircularSec: Number(values.timeCircularSec) || 0,
      timeCncSec: Number(values.timeCncSec) || 0,
      timeCantSec: Number(values.timeCantSec) || 0,
      timeAssemblySec: Number(values.timeAssemblySec) || 0,
      active: values.active,
    });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[min(95vw,520px)] bg-background p-4">
        <SheetTitle className="text-lg font-semibold">
          {initial
            ? t('hardwareAdmin.edit', { defaultValue: 'Edit hardware' })
            : t('hardwareAdmin.create', { defaultValue: 'New hardware' })}
        </SheetTitle>
        <Form form={form} onSubmit={onValid} className="mt-4 space-y-3">
          <TextField name="name" label={t('common.name', { defaultValue: 'Name' })} />
          <TextField
            name="offerName"
            label={t('catalog.offerName', { defaultValue: 'Offer Name' })}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField name="internalCode" label="Cod Intern" />
            <TextField name="sku" label="Cod Produs" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              name="manufacturer"
              label={t('common.manufacturer', { defaultValue: 'Manufacturer' })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              name="pricePerPiece"
              label={t('hardwareAdmin.pricePerPiece', { defaultValue: 'Price / piece' })}
            />
            <SelectField
              name="currency"
              label={t('common.currency', { defaultValue: 'Currency' })}
              options={CURRENCY_OPTIONS}
            />
          </div>
          {/* The default markup every use of this fitting starts from. Items
              already quoted keep the number they were created with. */}
          <TextField
            name="coefficient"
            label={t('materialsAdmin.coefficient', { defaultValue: 'Default coefficient' })}
            type="number"
            inputMode="decimal"
            min={0.01}
            step={0.1}
          />
          <div className="grid grid-cols-2 gap-3">
            <TextField
              name="timeCircularSec"
              {...nonNegativeIntegerFieldProps}
              label={t('hardwareAdmin.timeCircularSec', { defaultValue: 'Circular (s)' })}
            />
            <TextField
              name="timeCncSec"
              {...nonNegativeIntegerFieldProps}
              label={t('hardwareAdmin.timeCncSec', { defaultValue: 'CNC (s)' })}
            />
            <TextField
              name="timeCantSec"
              {...nonNegativeIntegerFieldProps}
              label={t('hardwareAdmin.timeCantSec', { defaultValue: 'Cant (s)' })}
            />
            <TextField
              name="timeAssemblySec"
              {...nonNegativeIntegerFieldProps}
              label={t('hardwareAdmin.timeAssemblySec', { defaultValue: 'Assembly (s)' })}
            />
          </div>
          <CheckboxField name="active" label={t('common.active', { defaultValue: 'Active' })} />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit">{t('common.save', { defaultValue: 'Save' })}</Button>
          </div>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
